import React, { useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { useToasts } from "react-toast-notifications";
import { getApiUrl } from "src/main_app/utils/config-utility";
import { authService } from "src/base/services";
import moment from "moment";
import axios from "axios";
import { saveAs } from "file-saver";
import DatePicker from "react-datepicker";

// The export streams back as a blob, so a failure arrives as a blob too and has
// to be read before its message can be shown. Worth doing here because this
// report depends on Mixpanel credentials that can be missing or expired, and a
// downloaded file full of JSON is a confusing way to find that out.
const readErrorMessage = async (error) => {
  const data = error && error.response && error.response.data;

  try {
    const text = data instanceof Blob ? await data.text() : JSON.stringify(data);
    const parsed = JSON.parse(text);
    const message =
      parsed &&
      parsed.error &&
      parsed.error.messages &&
      parsed.error.messages[0] &&
      parsed.error.messages[0].message;

    return message || "Could not generate the Mixpanel report";
  } catch (parseError) {
    return "Could not generate the Mixpanel report";
  }
};

// The server names the file after the tenant and range; fall back to a generic
// name if the header is missing.
const filenameFrom = (res, fallback) => {
  const header = res.headers && res.headers["content-disposition"];
  const match = header && /filename="?([^";]+)"?/i.exec(header);
  return match ? match[1] : fallback;
};

const ExportMixpanel = ({ showModal, handleActionsClose }) => {
  if (!showModal) return null;

  const { addToast } = useToasts();
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [isEmptyFromDate, setIsEmptyFromDate] = useState(false);
  const [isEmptyToDate, setIsEmptyTodDate] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  const resetStates = () => {
    setFromDate(null);
    setToDate(null);
    setIsEmptyTodDate(false);
    setIsEmptyFromDate(false);
  };

  const ExportHandler = async () => {
    const fromDateParam = moment(fromDate).format("YYYY-MM-DD");
    const toDateParam = moment(toDate).format("YYYY-MM-DD");

    const isFromDateValid = moment(fromDate).isValid();
    const isToDateValid = moment(toDate).isValid();
    const isFromDateBeforeToDate = moment(fromDate).isBefore(moment(toDate));

    if (!isFromDateValid) {
      addToast("Generate Report item field must not be empty", {
        appearance: "error",
        autoDismiss: true,
        autoDismissTimeout: 3000,
      });
      setIsEmptyFromDate(true);
      return;
    }

    if (!isToDateValid) {
      addToast("Generate Report item field must not be empty", {
        appearance: "error",
        autoDismiss: true,
        autoDismissTimeout: 3000,
      });
      setIsEmptyTodDate(true);
      return;
    }

    if (!isFromDateBeforeToDate) {
      addToast("From date must be before the to date", {
        appearance: "error",
        autoDismiss: true,
        autoDismissTimeout: 3000,
      });
      return;
    }

    // "Data exported" is tracked by the API once the file has been built, so it
    // is not sent from here as well.

    // The API pulls the raw events from Mixpanel before building the workbook,
    // which takes long enough that the modal has to stay open and say so.
    setIsGenerating(true);

    try {
      const res = await axios.get(
        `${getApiUrl()}/export/report-mixpanel/xlsx?&token=${authService.getAccessToken()}&from=${fromDateParam}&to=${toDateParam}&source=reports_page`,
        { responseType: "blob" }
      );

      saveAs(
        res.data,
        filenameFrom(
          res,
          `Aquo_Mixpanel_Report_${fromDateParam}_${toDateParam}.xlsx`
        )
      );
      handleActionsClose();
      resetStates();
    } catch (error) {
      addToast(await readErrorMessage(error), {
        appearance: "error",
        autoDismiss: true,
        autoDismissTimeout: 5000,
      });
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Modal show={showModal} onHide={handleActionsClose} size={"lg"}>
      <Modal.Header closeButton>
        <Modal.Title>Generate report - Mixpanel</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <form id="customer-add-link-form" className="">
          <div className="row">
            <div className="col-md-6">
              <div
                className={`form-group ${isEmptyFromDate ? "has-error" : ""}`}
              >
                <label>From date</label>
                <DatePicker
                  selected={fromDate}
                  dateFormat="dd MMM yyyy"
                  onChange={(date) => setFromDate(date)}
                />
              </div>
            </div>
            <div className="col-md-6">
              <div className={`form-group ${isEmptyToDate ? "has-error" : ""}`}>
                <label>To date</label>
                <DatePicker
                  selected={toDate}
                  dateFormat="dd MMM yyyy"
                  onChange={(date) => setToDate(date)}
                />
              </div>
            </div>
          </div>
          <Button
            variant="primary"
            onClick={ExportHandler}
            disabled={isGenerating}
          >
            {isGenerating ? "GENERATING…" : "GENERATE REPORT"}
          </Button>
        </form>
      </Modal.Body>
    </Modal>
  );
};

export default ExportMixpanel;
